import type { Campaign, LandingPage } from '@/types/campaign';
import { normalizeUrl } from './urlUtils';

interface UrlObject {
  url: string;
  flouri_scraping_enabled?: boolean;
}

interface LandingPageData {
  uniqueUrls: string[];
  urlDetails: LandingPage[];
}

// Internal interfaces for items that may have URL lists (used for type assertions)
// These are more permissive than the strict Campaign types to handle API variations
interface UrlItem {
  final_urls?: Array<UrlObject | string>;
  final_url_list?: string | string[];
  flouri_scraping_list?: boolean[];
}

interface AssetGroupItem extends UrlItem {
  asset_group_name?: string;
  asset_group_id?: string;
  mobile_final_url_list?: string;
}

interface AdItem extends UrlItem {
  ad_id?: string;
}

interface KeywordItem extends UrlItem {
  keyword_id?: string;
  keyword_text?: string;
}

/**
 * Helper function to extract URLs from an item (keyword, ad, or asset group)
 * Handles both new structure (final_urls array of objects) and legacy structure (final_url_list)
 */
function getUrlsFromItem(item: UrlItem): UrlObject[] {
  // New structure: final_urls array of objects or strings
  if (item.final_urls && Array.isArray(item.final_urls)) {
    return item.final_urls.map((urlItem) => {
      if (typeof urlItem === 'string') {
        return { url: urlItem, flouri_scraping_enabled: false };
      }
      return urlItem;
    });
  }

  // Legacy structure: final_url_list (string or array of strings)
  if (item.final_url_list) {
    const parsedUrls = parseUrlField(item.final_url_list);
    const scrapingPermissions = item.flouri_scraping_list || [];
    return parsedUrls.map((url, index) => ({
      url: url,
      flouri_scraping_enabled: scrapingPermissions[index] || false
    }));
  }

  return [];
}

/**
 * Parse URL field from various formats (string, JSON array, comma-separated, etc.)
 * All URLs are normalized (trailing slashes removed) for consistency.
 */
function parseUrlField(urlField: string | string[] | unknown): string[] {
  if (!urlField || urlField === '') {
    return [];
  }

  const urlStr = String(urlField).trim();

  // Handle empty sets/lists
  if (urlStr === '{}' || urlStr === '[]') {
    return [];
  }

  // Handle set notation {url1, url2}
  if (urlStr.startsWith('{') && urlStr.endsWith('}')) {
    const urlsStr = urlStr.slice(1, -1); // Remove curly braces
    return urlsStr.split(',').map(url => normalizeUrl(url.trim())).filter(url => url);
  }

  // Handle list notation ["url1", "url2"]
  if (urlStr.startsWith('[') && urlStr.endsWith(']')) {
    try {
      const urls = JSON.parse(urlStr);
      const parsedUrls = Array.isArray(urls) ? urls : [urls];
      return parsedUrls.map(url => normalizeUrl(url));
    } catch {
      // If JSON parsing fails, try manual parsing
      const urlsStr = urlStr.slice(1, -1); // Remove square brackets
      return urlsStr.split(',').map(url => normalizeUrl(url.trim().replace(/"/g, ''))).filter(url => url);
    }
  }

  // Handle plain string
  return [normalizeUrl(urlStr)];
}

/**
 * Create a simple hash for URL identification (for keys/IDs)
 */
export function createSimpleHash(url: string): string {
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    const char = url.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Extract landing page URLs from campaign data
 * This function traverses the campaign structure to find all final_urls
 * in keywords, ads, and asset groups
 */
export function extractLandingPageUrls(campaign: Campaign): LandingPageData {
  const campaignType = campaign.campaign_type || '';
  const urls = new Set<string>(); // Use Set to automatically deduplicate
  const urlDetails: LandingPage[] = []; // Store URL with context for UI

  // Build a lookup of authorized URLs from campaign.landing_pages
  // This is the source of truth for authorization status (shared across campaigns)
  const authorizedUrlLookup = new Map<string, boolean>();
  if (campaign.landing_pages && Array.isArray(campaign.landing_pages)) {
    campaign.landing_pages.forEach((page) => {
      if (page.url) {
        const normalizedPageUrl = normalizeUrl(page.url);
        authorizedUrlLookup.set(normalizedPageUrl, page.authorized ?? false);
      }
    });
  }

  // Helper to check if a URL is authorized (checks landing_pages first, then flouri_scraping_enabled)
  const isUrlAuthorized = (url: string, flouri_scraping_enabled?: boolean): boolean => {
    const normalizedUrl = normalizeUrl(url);
    // Check landing_pages first (this is synced across campaigns)
    if (authorizedUrlLookup.has(normalizedUrl)) {
      return authorizedUrlLookup.get(normalizedUrl) ?? false;
    }
    // Fall back to per-item flouri_scraping_enabled
    return flouri_scraping_enabled ?? false;
  };

  // Meta branch: walk `ad_sets[*].ads[*]`. The backend constructor at
  // `src/flouri/account_configuration.py:construct_app_configuration_json__meta_ads`
  // attaches `final_urls: [{url, flouri_scraping_enabled}]` to each ad
  // dict (pulled from the ad's creative `link_url` /
  // `object_story_spec.link_data.link`). Authorization storage in
  // `campaign.landing_pages` is platform-agnostic — Meta reuses the
  // same persistence path as Google.
  //
  // Short-circuit on `platform_source` alone (not also on
  // `ad_sets` truthiness): a Meta campaign without `ad_sets`
  // must NOT fall through to the Google walkers below — pre-A3
  // saved-config rows could carry stale `ads_group_list` that would
  // pollute results otherwise.
  if (campaign.platform_source === 'Meta Ads') {
    (campaign.ad_sets ?? []).forEach((adSet) => {
      const adSetName = adSet.ad_set_name ?? 'Unnamed Ad Set';
      // `MetaAdSet.ads` is typed as `MetaAd[]` which does not carry
      // `final_urls` on the public API surface — but the saved-config
      // entry does. Cast to the URL-bearing shape.
      const ads = (adSet.ads ?? []) as unknown as AdItem[];
      ads.forEach((ad) => {
        const urlObjs = getUrlsFromItem(ad);
        urlObjs.forEach((urlObj) => {
          const url = normalizeUrl(urlObj.url);
          if (url && !urls.has(url)) {
            urls.add(url);
            urlDetails.push({
              url,
              hash: createSimpleHash(url),
              authorized: isUrlAuthorized(url, urlObj.flouri_scraping_enabled),
              source_type: 'ad',
              source_name: adSetName,
              source_id: ad.ad_id,
            });
          }
        });
      });
    });
  } else if (campaignType.includes('PERFORMANCE_MAX')) {
    // Performance Max: URLs at asset group level
    if (campaign.asset_group_list) {
      campaign.asset_group_list.forEach((assetGroup) => {
        // Cast to internal type that supports all URL field variations
        const assetGroupItem = assetGroup as unknown as AssetGroupItem;
        const urlObjs = getUrlsFromItem(assetGroupItem);
        urlObjs.forEach((urlObj) => {
          // Normalize URL to ensure consistent deduplication (removes trailing slashes)
          const url = normalizeUrl(urlObj.url);
          if (url && !urls.has(url)) {
            urls.add(url);
            urlDetails.push({
              url: url,
              hash: createSimpleHash(url),
              authorized: isUrlAuthorized(url, urlObj.flouri_scraping_enabled),
              source_type: 'asset_group',
              source_name: assetGroup.asset_group_name || 'Unnamed Asset Group',
              source_id: assetGroup.asset_group_id
            });
          }
        });

        // Also check mobile URLs if available
        if (assetGroupItem.mobile_final_url_list) {
          const parsedMobileUrls = parseUrlField(assetGroupItem.mobile_final_url_list);
          parsedMobileUrls.forEach((url: string) => {
            if (url && !urls.has(url)) {
              urls.add(url);
              urlDetails.push({
                url: url,
                hash: createSimpleHash(url),
                authorized: isUrlAuthorized(url, false),
                source_type: 'asset_group_mobile',
                source_name: assetGroup.asset_group_name || 'Unnamed Asset Group',
                source_id: assetGroup.asset_group_id
              });
            }
          });
        }
      });
    }
  } else if (campaign.ads_group_list) {
    // Search and Shopping: URLs primarily at ad level
    // Keywords can have URLs to override the ad's URL, but this is less common
    campaign.ads_group_list.forEach((adGroup) => {
      const adGroupName = adGroup.ads_group_name || adGroup.ad_group_name || 'Unnamed Ad Group';
      const adGroupUrls = new Set<string>(); // Track URLs from ads in this ad group

      // First, check ads (primary source of URLs)
      // Handle both 'ads' and 'ads_list' field names for compatibility
      const ads = adGroup.ads || adGroup.ads_list;
      if (ads && ads.length > 0) {
        ads.forEach((ad) => {
          // Cast to internal type that supports all URL field variations
          const adItem = ad as unknown as AdItem;
          const urlObjs = getUrlsFromItem(adItem);
          urlObjs.forEach((urlObj) => {
            // Normalize URL to ensure consistent deduplication (removes trailing slashes)
            const url = normalizeUrl(urlObj.url);
            if (url && !urls.has(url)) {
              urls.add(url);
              adGroupUrls.add(url); // Track for keyword comparison
              urlDetails.push({
                url: url,
                hash: createSimpleHash(url),
                authorized: isUrlAuthorized(url, urlObj.flouri_scraping_enabled),
                source_type: 'ad',
                source_name: adGroupName, // Just show ad group name, not individual ad details
                source_id: ad.ad_id
              });
            }
          });
        });
      }

      // Then check keywords for URL overrides (only if different from ad URLs)
      if (adGroup.keywords_list) {
        adGroup.keywords_list.forEach((keyword) => {
          // Cast to internal type that supports all URL field variations
          const keywordItem = keyword as unknown as KeywordItem;
          const urlObjs = getUrlsFromItem(keywordItem);
          urlObjs.forEach((urlObj) => {
            // Normalize URL to ensure consistent deduplication (removes trailing slashes)
            const url = normalizeUrl(urlObj.url);
            // Only include keyword URL if it's new (not already captured from ads)
            if (url && !urls.has(url)) {
              urls.add(url);
              urlDetails.push({
                url: url,
                hash: createSimpleHash(url),
                authorized: isUrlAuthorized(url, urlObj.flouri_scraping_enabled),
                source_type: 'keyword_override',
                source_name: `${adGroupName} (override: ${keyword.keyword_text || 'keyword'})`,
                source_id: keyword.keyword_id
              });
            }
          });
        });
      }
    });
  }

  return {
    uniqueUrls: Array.from(urls),
    urlDetails: urlDetails
  };
}
